// Presentational "bands" for the marketplace storefront chrome — the hero
// header and the footer. Extracted (Task 3 of the storefront-content
// integration) so the operator-facing <StorePreview> (portal editor) and the
// customer-facing <StorefrontShell> (marketplace embed) could render through
// one slice.
//
// ⚠️ That is now true of the FOOTER ONLY. `StoreFooterBand` still has both
// consumers, but `StoreHeroBand` has only ONE — <StorePreview>. The customer
// storefront renders its own `StorefrontHero` (StorefrontShell.tsx), which the
// D10 redesign gave backdrop/copy anatomy this plain band lacks; the portal
// preview keeps the simple band until it is redesigned in its own task.
//
// So: changing StoreHeroBand changes the OPERATOR PREVIEW ONLY and will make
// the two surfaces diverge further, not converge. Verify the consumer set with
// `grep -rn StoreHeroBand src/` before assuming a change reaches customers.
//
// Contract:
//   - Pure / props-driven. No fetching, no portal imports.
//   - Tokens-driven host-CSS isolation: every color/size comes from
//     useEmbedTokens(); inline styles only (embed convention).
//   - Fail-open: each band returns null when its required content is absent,
//     so callers can mount them unconditionally and let the band decide.

import { useEmbedTokens, EmbedButton } from "../../embed-shared/primitives";
import { useReducedMotion, motionSafe } from "../../embed-shared/theme";

export interface StoreHeroBandProps {
  headline: string;
  subheadline?: string;
  ctaText?: string;
  ctaUrl?: string;
  /**
   * In-app navigation handler for the hero CTA. When supplied the CTA renders
   * as a <button> (the catalog is in-app, not at /products) instead of an
   * <a href>. Takes priority over ctaUrl — if both are set, onCtaClick wins
   * because the embed catalog route is always the intended destination.
   */
  onCtaClick?: () => void;
}
export function StoreHeroBand({ headline, subheadline, ctaText, ctaUrl, onCtaClick }: StoreHeroBandProps) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  if (!headline) return null;

  // Shared CTA style — used identically by the <button> (onCtaClick) and <a>
  // (external ctaUrl) variants so the visual is pixel-identical regardless of
  // element. lineHeight matches height so text vertically centers inside the
  // fixed-height inline-block (inline-flex centering from EmbedButton does
  // not apply to inline-block).
  const ctaStyle = {
    display: "inline-block",
    textDecoration: "none",
    background: t.colors.primary,
    // onAction, not white: the design-system dark on-action is near-black.
    color: t.colors.onAction,
    fontFamily: "inherit",
    fontSize: t.fontSize.sm,
    fontWeight: 600,
    height: t.buttonHeight,
    lineHeight: `${t.buttonHeight}px`,
    padding: "0 18px",
    borderRadius: t.borderRadiusControl,
    border: "1px solid transparent",
    cursor: "pointer",
    transition: motionSafe(reduced, "background .12s,border-color .12s,color .12s"),
  } as const;

  return (
    <section
      data-testid="store-hero-band"
      style={{
        backgroundColor: t.colors.primaryTint,
        padding: `${t.spacing.xl}px ${t.spacing.lg}px`,
        textAlign: "center",
        borderRadius: t.borderRadiusLg,
        marginBottom: t.spacing.lg,
      }}
    >
      <h1 style={{ margin: `0 0 ${t.spacing.xs}px`, fontSize: t.fontSize.xxl, fontWeight: 700, color: t.colors.text }}>
        {headline}
      </h1>
      {subheadline ? (
        <p style={{ margin: `0 0 ${t.spacing.md}px`, color: t.colors.textSecondary }}>{subheadline}</p>
      ) : null}
      {onCtaClick && ctaText ? (
        <button type="button" onClick={onCtaClick} style={ctaStyle}>
          {ctaText}
        </button>
      ) : ctaText && ctaUrl ? (
        <a href={ctaUrl} style={ctaStyle}>
          {ctaText}
        </a>
      ) : ctaText ? (
        <EmbedButton variant="primary">{ctaText}</EmbedButton>
      ) : null}
    </section>
  );
}

export interface StoreFooterBandProps {
  footerText?: string;
  aboutText?: string;
  contactEmail?: string;
  contactPhone?: string;
}
export function StoreFooterBand({ footerText, aboutText, contactEmail, contactPhone }: StoreFooterBandProps) {
  const t = useEmbedTokens();
  if (!footerText && !aboutText && !contactEmail && !contactPhone) return null;
  return (
    <footer
      data-testid="store-footer-band"
      style={{
        borderTop: `1px solid ${t.colors.borderSubtle}`,
        marginTop: t.spacing.xl,
        padding: `${t.spacing.lg}px ${t.spacing.md}px`,
        textAlign: "center",
        color: t.colors.textSecondary,
        fontSize: t.fontSize.sm,
        display: "flex",
        flexDirection: "column",
        gap: t.spacing.xs,
      }}
    >
      {footerText ? <p style={{ margin: 0 }}>{footerText}</p> : null}
      {aboutText ? <p style={{ margin: 0 }}>{aboutText}</p> : null}
      {contactEmail || contactPhone ? (
        <p style={{ margin: 0 }}>
          {contactEmail ? <a href={`mailto:${contactEmail}`} style={{ color: t.colors.textSecondary }}>{contactEmail}</a> : null}
          {contactEmail && contactPhone ? " · " : null}
          {contactPhone ? <span>{contactPhone}</span> : null}
        </p>
      ) : null}
    </footer>
  );
}
